import styled from '@emotion/styled'
import { ReactNode } from 'react'

import { RequiredIndicator } from './RequiredIndicator'
import { Color, theme } from '../../constants'
import { StyleProps, createStyles } from '../../utils'
import { Icon, IconName } from '../Icon'
import { Paragraph } from '../Paragraph'

const LabelElement = styled.label<Partial<LabelProps>>((props) => ({
  alignItems: props.placement === 'top' ? undefined : 'center',
  display: 'flex',
  flexDirection:
    props.placement === 'top'
      ? 'column'
      : props.placement === 'right'
        ? 'row-reverse'
        : 'row',
  width: props.fullWidth ? '100%' : undefined,
  ...createStyles(props.styles),
}))

const LabelDescription = styled(Paragraph)({
  marginBottom: 8,
})

const LabelText = styled.span<Partial<LabelProps>>((props) => ({
  alignItems: 'center',
  color: props.color ?? Color.BLUE,
  cursor: props.cursor,
  display: 'flex',
  fontSize: props.size === 'sm' ? theme.font.size[6] : theme.font.size[6],
  fontWeight: theme.font.weight.medium,
  gridGap: 8,
  marginBottom: props.placement === 'top' ? 8 : 0,
  marginLeft: props.placement === 'right' ? 8 : 0,
  marginRight: props.placement === 'left' ? 8 : 0,
}))

export type LabelPlacement = 'right' | 'top' | 'left'

export type LabelProps = {
  children?: ReactNode
  className?: string
  color?: string
  cursor?: 'not-allowed' | 'pointer'
  description?: string
  for?: string
  fullWidth?: boolean
  hideRequiredIndicator?: boolean
  icon?: IconName
  placement?: LabelPlacement
  required?: boolean
  size?: 'sm' | 'md'
  styles?: StyleProps
  text: string
}

export const Label = ({ placement = 'top', ...props }: LabelProps) => {
  return (
    <LabelElement
      className={props.className}
      styles={props.styles}
      fullWidth={props.fullWidth}
      htmlFor={props.for}
      placement={placement}
    >
      <LabelText
        className="label-text"
        color={props.color}
        cursor={props.cursor}
        placement={placement}
        size={props.size}
      >
        {props.icon && (
          <Icon
            backgroundColor={Color.LIGHTER_BLUE}
            name={props.icon}
            round
            size="sm"
          />
        )}
        <span>
          {props.text}
          {!props.hideRequiredIndicator && (
            <RequiredIndicator required={!!props.required} />
          )}
        </span>
      </LabelText>
      {props.description && (
        <LabelDescription noMargin>{props.description}</LabelDescription>
      )}
      {props.children}
    </LabelElement>
  )
}
